/**
 * Pure predicate: is a Rumor's prerequisite Whisper-set fully held by the
 * given journal? Returns `true` iff every id in `rumor.unlockedByWhisperIds`
 * is present in `journal.whispers`. An empty prerequisite list returns `true`
 * (vacuously satisfied).
 *
 * This is the seed of the WhisperService's per-insertion combination check.
 * Kept here as a pure, dependency-free helper so the server and the client
 * can both use it without pulling in either side's runtime.
 *
 * @param journal — the character's journal to inspect.
 * @param rumor   — the candidate Rumor whose prerequisites we're testing.
 * @returns `true` if every required Whisper id is present; otherwise `false`.
 */
export function checkRumorUnlock(journal, rumor) {
    if (rumor.unlockedByWhisperIds.length === 0)
        return true;
    const held = new Set(journal.whispers.map((w) => w.id));
    for (const requiredId of rumor.unlockedByWhisperIds) {
        if (!held.has(requiredId))
            return false;
    }
    return true;
}
// ── Server ��� client journal messages (B-011) ───��────────────────────────────
//
// Wire constants for the journal-side broadcasts. Today only the Dreaming
// Whisper accrual path emits these (`AttendanceLedger.handleLogin` →
// `JournalLedger.recordWhispers` → OverworldRoom broadcast); future paths
// (shrine draws, expedition events, world events) will reuse the same
// message shapes once their generation paths land. The client-side Journal
// scene (B-010) listens for these to refresh its tabs without a full read.
/** Wire constant — server broadcasts this when one or more Whispers land in
 *  a character's journal. Payload: {@link WhisperGrantedPayload}. */
export const MSG_JOURNAL_WHISPER_GRANTED = 'journal_whisper_granted';
/** Wire constant — server broadcasts this when a Whisper insertion satisfies
 *  a Rumor's prerequisite set for the first time. Payload:
 *  {@link RumorUnlockedPayload}. Always emitted alongside the
 *  {@link MSG_JOURNAL_WHISPER_GRANTED} that caused the unlock — clients can
 *  treat them as one logical event when both arrive in the same frame. */
export const MSG_JOURNAL_RUMOR_UNLOCKED = 'journal_rumor_unlocked';
// ── Client ↔ server journal snapshot pull (B-010) ────────────────────────────
//
// The client journal scene (B-010) refreshes its tab content on open and
// after `MSG_JOURNAL_WHISPER_GRANTED` / `MSG_JOURNAL_RUMOR_UNLOCKED` arrive.
// To avoid a stale render when the client lands fresh (no whispers granted
// this session, scene opened cold), the client pulls a full snapshot via
// `MSG_JOURNAL_REQUEST_SNAPSHOT`; the server replies with `MSG_JOURNAL_SNAPSHOT`
// carrying the same `JournalSnapshot` shape `JournalLedger.getJournalForCharacter`
// already returns. No payload on the request — the server already knows the
// requesting character via the Colyseus session.
/** Wire constant — client requests a fresh journal snapshot. Payload: empty
 *  object. The server resolves the character from the requesting client. */
export const MSG_JOURNAL_REQUEST_SNAPSHOT = 'journal_request_snapshot';
/** Wire constant — server replies with the requesting character's journal
 *  snapshot. Payload: {@link JournalSnapshot}. */
export const MSG_JOURNAL_SNAPSHOT = 'journal_snapshot';
// ── Dream-Weaving server seam (B-038) ───────────────────────────────────────
//
// Round 9+ ritual per `docs/design/dream-weaving.md`. The full ritual has a
// pre-sleep modal, two materials (cloth + ink), Vocation modifiers, paired-
// dream variants, and a 2× rate buff. B-038 lands the **server seam** only:
// the message + handler that lets the client submit a free-text intent while
// parked at a shrine, and the intent-biased Whisper picker that consumes the
// stored intent on the next Dreaming-state accrual.
//
// Out of scope this task (per backlog B-038 + brief):
//   - Client UI (text input / tag picker) — separate follow-up.
//   - Material consumption (cloth / ink) — `dream-weaving.md` §1.4.
//   - 2× accrual rate — `dream-weaving.md` §2.1.
//   - Paired weaves — `dream-weaving.md` §7.
//   - Vocation modifiers — `dream-weaving.md` §10.
//   - Dreamt pet items — `dream-weaving.md` §6.
//
// What lands today:
//   - Server-side persistence of an `intent` string per character.
//   - Tag-bias scoring on the existing Dreaming Whisper picker.
//   - Validation that the player is `ParkedShrine` before applying.
//
// The bias is *tendency*, not *guarantee* — `dream-weaving.md` §2.1 calls
// this out explicitly. Without an intent set, the picker falls through to
// the existing demo round-robin behaviour.
/** Wire constant — client submits a free-text dream-weaving intent. The
 *  server validates that the requesting character is currently
 *  `AttendanceState.ParkedShrine` (B-038 server seam; the home-hearth
 *  parking variant per `dream-weaving.md` §1.2 lands with the full ritual
 *  in round 9+). Payload: {@link DreamWeaveRequest}. */
export const MSG_DREAM_WEAVE = 'dream_weave';
/** Wire constant — server confirms the dream-weaving intent was cleared
 *  (e.g. the player stood up, or explicitly cleared the intent). Payload:
 *  {@link DreamIntentClearedEvent}. The recipient client may use this to
 *  fade the spool-icon affordance on the parked-shrine HUD. */
export const MSG_DREAM_INTENT_CLEARED = 'dream_intent_cleared';
/** Hard cap on raw intent text length at the wire boundary. Anything
 *  longer is truncated server-side; the bias picker only consults the
 *  first three recognized tokens anyway (`dream-weaving.md` §4.4). */
export const DREAM_INTENT_MAX_LENGTH = 140;
// ── Relic grant messages (KK — round-8) ────────────────────────────────────
//
// Incremental grant broadcast for Relics awarded at the conclusion of an
// Expedition. The client accumulates these into the RelicsTab render list.
// Full Relic data lives in `CharacterJournal.relics` (persisted server-side);
// this is the delta message that lets the client update without a full journal
// re-pull.
/** Wire constant — server broadcasts when a Relic is awarded to a character
 *  at the conclusion of an Expedition. Payload: {@link RelicGrantedPayload}. */
export const MSG_RELIC_GRANTED = 'relic_granted';
// ── Reliquary messages (Round 20 Phase F) ─────────────────────────────────
//
// The Reliquary is a server-wide shared view of every Relic ever granted to
// any character. Every player sees the same Reliquary. The client requests
// the full set on scene open; the server replies with the aggregate.
/** Client → Server request for the server-wide Reliquary snapshot. */
export const MSG_RELIQUARY_REQUEST = 'reliquary_request';
/** Server → Client reply carrying the full Reliquary contents. */
export const MSG_RELIQUARY_SNAPSHOT = 'reliquary_snapshot';
//# sourceMappingURL=types.js.map